import { StrictMode, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { initializeSentry } from './observability';
import { surfaceConfig, type AppSurface } from './appSurface';
import { installServiceWorkerUpdates } from './utils/serviceWorkerUpdates';
import { recoverFromStaleAssetError } from './lib/staleAssetRecovery';
import { isModuleLoadError } from './utils/moduleLoadError';

export interface BootstrapAppOptions {
  surface: AppSurface;
  AppComponent: ComponentType;
}

/**
 * Establish one opaque device installation ID per app origin. The dedicated
 * companion host cannot read the root host's cookie, so it must establish its
 * own server-scoped installation before it can bridge auth or claim alerts.
 */
export async function ensureDeviceCookieForSurface(_surface: AppSurface): Promise<void> {
  // Preserve the surface argument at the public boundary: both supported
  // surfaces now establish an installation on their own origin.
  void _surface;
  const response = await fetch('/api/auth/device/ensure', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Valentine-Root-Bootstrap': '1' },
  });
  if (!response.ok) {
    throw new Error('Unable to establish device installation');
  }
}

async function registerSurfaceWorker(surface: AppSurface): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  const config = surfaceConfig(surface);
  try {
    const registration = await navigator.serviceWorker.register(config.workerUrl, {
      scope: config.workerScope,
      updateViaCache: 'none',
    });
    if (!import.meta.env.DEV) {
      installServiceWorkerUpdates({ registration });
    }
  } catch (error) {
    console.error(`${surface} service worker registration failed:`, error);
  }
}

let vitePreloadHandlerRegistered = false;

/**
 * Register global listener for Vite dynamic preload errors (Vite CSS / JS chunk 404s).
 * Catches preload failures outside React error boundaries and initiates recovery.
 */
export function registerVitePreloadErrorHandler(): () => void {
  if (typeof window === 'undefined' || vitePreloadHandlerRegistered) return () => {};
  vitePreloadHandlerRegistered = true;

  const handler = (event: Event) => {
    const rawPayload = (event as { payload?: unknown }).payload;
    if (!rawPayload) return;

    const error = rawPayload instanceof Error ? rawPayload : new Error(String(rawPayload));
    if (!isModuleLoadError(error)) return;

    if (event.cancelable) {
      event.preventDefault();
    }

    void recoverFromStaleAssetError(
      error,
      window.sessionStorage,
      () => window.location.reload(),
    ).then((recovered) => {
      if (!recovered) {
        Sentry.captureException(error);
      }
    }).catch((recoveryError) => {
      console.warn('vite:preloadError recovery failed:', recoveryError);
      Sentry.captureException(error);
    });
  };

  window.addEventListener('vite:preloadError', handler);
  return () => {
    window.removeEventListener('vite:preloadError', handler);
    vitePreloadHandlerRegistered = false;
  };
}

/** Sets up the common observability and worker boundary for a PWA surface. */
export async function bootstrapApp({ surface, AppComponent }: BootstrapAppOptions): Promise<void> {
  initializeSentry();
  registerVitePreloadErrorHandler();
  Sentry.setTag('app_surface', surface);

  await ensureDeviceCookieForSurface(surface);

  const root = document.getElementById('root');
  if (!root) throw new Error('Missing #root application element');
  createRoot(root).render(
    <StrictMode>
      <AppComponent />
    </StrictMode>,
  );

  await registerSurfaceWorker(surface);
}
